import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useAvailableRaynetUsers } from '@/hooks/useRaynetUsers';
import { LanguageSelector } from '@/components/LanguageSelector';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useTranslation } from 'react-i18next';

export function LoginPage() {
  const { t } = useTranslation('common');
  const [selectedUser, setSelectedUser] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const { data: usersData, isLoading: isLoadingUsers } = useAvailableRaynetUsers();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!selectedUser || !password) {
      setError(t('validation.required'));
      return;
    }

    setIsLoading(true);
    
    try {
      await login(selectedUser, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  const users = usersData?.data || [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      {/* Language Selector a Theme Toggle v pravém horním rohu */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector variant="minimal" />
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/images/blaze-harmony-logo-2025.svg" 
              alt="Blaze Harmony Logo" 
              className="h-16 w-auto"
            />
          </div>
          <CardDescription>
            {t('login.form_title')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user">{t('login.user_label')}</Label>
              <select
                id="user"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoadingUsers || isLoading}
              >
                <option value="">
                  {isLoadingUsers ? t('login.loading_users') : t('login.select_user')}
                </option>
                {users.map(user => (
                  <option key={user.username} value={user.username}>
                    {user.full_name} ({user.username})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password_label')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.password_placeholder')}
                disabled={isLoading}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isLoadingUsers || !selectedUser || !password}
            >
              {isLoading ? t('login.logging_in') : t('login.login_button')}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-gray-600">
            <p>{t('login.info_text')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 